import React from "react"
import { Grid } from "./Components/CSSGrid/Grid"
import { GridArea } from "./Components/CSSGrid/GridArea"
import { useRecoilState, useRecoilValue } from "recoil"
import { layoutGroupsAtom, selectedLayoutAtom, selectedLayoutGroupAtom } from "./Atoms/Layout"
import { AreaConfig, Layout } from "./Types/Layout"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../../../Components/MobergButton/MobergButton"
import { MobergTheme } from "../../../../Components/MobergThemes/MobergColors"
import { MobergFontSize } from "../../../../Components/MobergFont/MobergFont"
import { useLayoutService } from "../../../../Hooks/useLayoutService"
import { MobergColumn, MobergRow } from "../../../../Moberg"
import { useAsyncTask } from "../../../../Hooks/useAsyncTask"
import { useDataReviewPermissions } from "../../../../Hooks/useDataReviewPermissions"
import { permissionTooltip } from "../../../../Utils/permissionUtils"
import { useSyncTime } from "./Hooks/useSyncTime"
import { currentPatientFileInfoAtom } from "./Atoms/PatientFile"
import { useCheckRecordingInfo } from "./Hooks/useCheckRecordingInfo"
import { useAnnotationSetupPrompt } from "./Hooks/useAnnotationSetupPrompt"
import { DataSource } from "./Types/DataSource"
import { useEventReviewNavigationCursorClickHandler } from "./Hooks/useEventReviewNavigationCursorClickHandler"

export const Visualizations = () => {
	const { addLayoutToLayoutGroup, createLayoutGroup } = useLayoutService()
	const [selectedLayoutGroup, setSelectedLayoutGroup] = useRecoilState(selectedLayoutGroupAtom)
	const layoutGroups = useRecoilValue(layoutGroupsAtom)
	const [selectedLayout, setSelectedLayout] = useRecoilState<Layout | null>(selectedLayoutAtom)
	const currentPatientFileInfo = useRecoilValue(currentPatientFileInfoAtom)

	
	const addDisplayTask = useAsyncTask(() => new Promise((resolve, reject) => {
		if (selectedLayoutGroup) {
			addLayoutToLayoutGroup(selectedLayoutGroup)
				.then(resolve)
				.catch(reject)
		} else {
			resolve(null)
		}
	}))

	const addDisplayGroupTask = useAsyncTask(() => createLayoutGroup("Personal"))

	const { canManageDataReview } = useDataReviewPermissions()
	const addGroupDisabledReason = permissionTooltip(canManageDataReview)
		|| (addDisplayGroupTask.isWaiting ? "Creating display group..." : "")
	const addDisplayDisabledReason = permissionTooltip(canManageDataReview)
		|| (addDisplayTask.isWaiting ? "Adding display..." : "")

	useAnnotationSetupPrompt()

	useSyncTime()
	useEventReviewNavigationCursorClickHandler()
	
	useCheckRecordingInfo({ 
		dataObjectId: currentPatientFileInfo.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number,
		intervalMilliseconds: 10_000
	})
	
	if (!selectedLayoutGroup && layoutGroups.length > 0) {
		setSelectedLayoutGroup(layoutGroups[0])
	}

	if (!selectedLayout && selectedLayoutGroup?.layouts && selectedLayoutGroup.layouts.length > 0) {
		setSelectedLayout(selectedLayoutGroup?.layouts[0])
	}

	const noDisplayText = !selectedLayoutGroup ? "No display group selected" : "No display selected"

	if (!selectedLayout) {
		return (
			<MobergColumn verticalAlign="center" expand={true}>
				<MobergRow horizontalAlign="center">
					<MobergColumn gap="16px" horizontalAlign="center">
						<h3 style={{ margin: 0 }}>{noDisplayText}</h3>

						{layoutGroups.length === 0 && (
							<MobergButton
								onClick={addDisplayGroupTask.run}
								disabled={!!addGroupDisabledReason}
								tooltip={addGroupDisabledReason}
								theme={MobergTheme.BLUE}
								variant={MobergButtonVariant.FILLED}
								fontSize={MobergFontSize.LARGE}
								shape={MobergButtonShape.WIDE}
							>
								Add display group
							</MobergButton>
						)}

						{selectedLayoutGroup?.layouts.length === 0 && (
							<MobergButton
								onClick={addDisplayTask.run}
								disabled={!!addDisplayDisabledReason}
								tooltip={addDisplayDisabledReason}
								theme={MobergTheme.BLUE}
								variant={MobergButtonVariant.FILLED}
								fontSize={MobergFontSize.LARGE}
								shape={MobergButtonShape.WIDE}>
								Add display
							</MobergButton>
						)}
					</MobergColumn>
				</MobergRow>
			</MobergColumn>
		)
	}

	const { cssGridTemplate, areas } = selectedLayout

	return (
		<Grid template={cssGridTemplate} gap={"4px"} padding={"4px"}>
			{areas.map((config: AreaConfig) => (
				<GridArea key={`${selectedLayout.id}-${config.area}`} {...config} shadow={true} />
			))}
		</Grid>
	)
}
